import { StarFilled } from '@ant-design/icons';
import { Alert, Divider, notification, Select } from 'antd';
import { Checkbox, Detail, Input, Loader } from 'components/common';
import { useAvailableFieldsCount } from 'components/ExportTemplates/hooks/useAvailableFieldsCount';
import { useTemplateMetadataFetch } from 'components/ExportTemplates/hooks/useTemplateMetadataFetch';
import { handleFailedExportNotification } from 'components/Search/Results/Actions/notifications';

import { useAuth, useGet } from 'hooks';
import { deburr } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportTemplateActions,
  exportTemplateSelectors,
} from 'reducers/exportTemplate/reducer';
import { exportTemplate } from 'services/api';
import styled from 'styled-components';
import {
  hasFullLicense,
  isAcademicFull,
  isFullTrial,
  isIndustryFull,
  noAcademicLicence,
  noIndustryLicence,
} from 'utils/helpers/license';
import { colours } from 'utils/theme';
import Alerts from './Alerts';
import {
  allTrial,
  downloadType as downloadTypeConstants,
  excludeAcademic,
  excludeIndustry,
  industryTrial,
  nonIndustry,
} from './constants';
import BaseFooter from './Footer';
import PreviouslyExportedMessage from './PreviouslyExportedMessage';
import StatsProgress from './StatsProgress';
import Total from './Total';
import useCheckExport from './useCheckExport';

const Container = styled.div`
  width: 360px;
  .exclude-previous-checkbox {
    .ant-checkbox + span {
      padding-right: 0;
      padding-left: 4px;
      font-size: 11px;
    }
  }
`;

const CustomiseFieldsLink = styled.button`
  color: ${colours.linkBlue};
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  margin-top: 15px;
  display: block;
  &:hover {
    text-decoration: underline;
  }
  &:disabled {
    color: ${colours.grey};
    cursor: not-allowed;
    &:hover {
      text-decoration: none;
    }
  }
`;

const FieldCount = styled.span`
  color: ${colours.grey};
  font-size: 12px;
`;

function Summary({
  transactionId,
  regenerateTransactionId,
  downloadType,
  onClose,
  getQueryBody,
  onExport,
  canUseCredits,
  crmLists,
  footer,
  className = '',
  style = {},
  showExportTemplateSection = false,
}) {
  const { user, hasLicense } = useAuth();

  // ========== Redux State ==========

  const dispatch = useDispatch();

  // Template state
  const selectedTemplateId = useSelector(
    exportTemplateSelectors.getSelectedTemplateId
  );
  const favouriteTemplateId = useSelector(
    exportTemplateSelectors.getFavouriteTemplateId
  );
  const mutationCount = useSelector(exportTemplateSelectors.getMutationCount);
  const oneTimeExport = useSelector(exportTemplateSelectors.getOneTimeExport);
  const dropdownTemplates = useSelector(
    exportTemplateSelectors.getDropdownTemplates
  );

  // User settings state
  const excludeFromExportTabsWithNoResults = useSelector(
    state => state.userSettings.excludeFromExportTabsWithNoResults
  );
  const excludePreviouslyExportedEmailsByDefault = useSelector(
    state => state.userSettings.excludePreviouslyExportedEmailsByDefault
  );

  // ========== Local State ==========

  const [fileName, setFileName] = useState(null);
  const [excludePreviouslyExported, setExcludePreviouslyExported] = useState(
    excludePreviouslyExportedEmailsByDefault
  );

  const [excludeEmptyTabsEnabled, setExcludeEmptyTabsEnabled] = useState(
    excludeFromExportTabsWithNoResults
  );
  const onExcludeEmptyTabsCheckboxChange = value => {
    setExcludeEmptyTabsEnabled(value);
  };

  const [
    showExcludePreviouslyExportedMessage,
    setShowExcludePreviouslyExportedMessage,
  ] = useState(false);

  // ========== HTTP ==========

  const metadataRes = useTemplateMetadataFetch();
  const [minimalListRes, fetchMinimalList] = useGet({
    url: '/api/export-templates/minimal-list',
    onMount: true,
    configuration: {
      onRequestSuccess: httpData => {
        dispatch(exportTemplateActions.minimalTemplatesReceived(httpData));
      },
    },
  });

  // If the mutation counter increments - it's our cue to refetch the templates
  const templateMutationsRef = useRef(0);
  if (templateMutationsRef.current !== mutationCount) {
    templateMutationsRef.current = mutationCount;
    fetchMinimalList();
  }

  const [checkExportRes, checkExportLoading, error] = useCheckExport(
    transactionId,
    downloadType,
    getQueryBody,
    user.disableExport,
    canUseCredits
  );

  const {
    totalEmails,
    previouslyExportedEmails,
    academicCreditsRequired,
    academicCreditsUsed,
    academicCreditsTotal,
    industryCreditsRequired,
    industryCreditsUsed,
    industryCreditsTotal,
    reachedAcademicCreditsLimit,
    isAboutToReachAcademicCreditsLimit,
    reachedIndustryCreditsLimit,
    isAboutToReachIndustryCreditsLimit,
    uncategorisedEmails,
    daysUntilReset,
    academicEmails,
    industryEmails,
    noEmailContacts,
  } = checkExportRes || {};

  const [, fetchSingleTemplate] = useGet({
    configuration: {
      onRequestSuccess: httpData => {
        dispatch(
          exportTemplateActions.existingTemplateEditStarted({
            response: httpData,
            context: 'search',
            hasLicense,
          })
        );
      },
      onRequestError: () => {
        notification.error({
          message: 'Failed to load template details',
          placement: 'bottomLeft',
          duration: 6,
        });
      },
    },
  });

  // ========== Derived State ==========

  const availableFieldsCount = useAvailableFieldsCount();

  const templateOptions = useMemo(() => {
    const options = [];
    const defaultOption = {
      value: 'all',
      searchText: 'scileads default',
      label: (
        <>
          SciLeads default{' '}
          <FieldCount>({availableFieldsCount} fields)</FieldCount>
        </>
      ),
    };

    if (dropdownTemplates) {
      // Favourite template goes first, then SciLeads default, then the rest
      // sorted naturally so digits precede letters and "Template 2" < "Template 10".
      const favourite = dropdownTemplates.find(
        t => t.id === favouriteTemplateId
      );
      if (favourite) {
        options.push({
          value: favourite.id,
          searchText: favourite.name.toLowerCase(),
          label: (
            <>
              {favourite.name}{' '}
              <FieldCount>({favourite.availableFieldCount} fields)</FieldCount>
              <StarFilled
                style={{ color: colours.gold, marginLeft: 6, fontSize: 12 }}
              />
            </>
          ),
        });
      }
      options.push(defaultOption);
      const rest = dropdownTemplates
        .filter(template => template.id !== favouriteTemplateId)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        );
      rest.forEach(template => {
        options.push({
          value: template.id,
          searchText: deburr(template.name.toLowerCase()),
          label: (
            <>
              {template.name}{' '}
              <FieldCount>({template.availableFieldCount} fields)</FieldCount>
            </>
          ),
        });
      });
    } else {
      options.push(defaultOption);
    }

    return options;
  }, [dropdownTemplates, availableFieldsCount, favouriteTemplateId]);

  // ========== Event Triggers ==========

  // This one gets triggered by export templates oneTimeExport button being pressed
  // in the modal. The payload is calculated and stored in 'oneTimeExport' as soon
  // as the button is pressed.
  useEffect(() => {
    if (oneTimeExport === null) return;
    dispatch(exportTemplateActions.oneTimeExportConsumed());
    const exportParams = {
      downloadType,
      fileName,
      transactionId,
      excludePreviouslyExported: excludePreviouslyExported ?? false,
      excludeEmptyTabsEnabled,
      excludeAcademic: !isAcademicFull(user) || reachedAcademicCreditsLimit,
      excludeIndustry: !isIndustryFull(user) || reachedIndustryCreditsLimit,
      exportTemplateSheets: oneTimeExport,
    };

    onClose();
    onExport(exportParams);
  }, [
    oneTimeExport,
    excludeEmptyTabsEnabled,
    dispatch,
    downloadType,
    fileName,
    transactionId,
    excludePreviouslyExported,
    user,
    reachedAcademicCreditsLimit,
    reachedIndustryCreditsLimit,
    onClose,
    onExport,
  ]);

  useEffect(() => {
    dispatch(
      exportTemplateActions.templateSelectionChanged(
        favouriteTemplateId ?? 'all'
      )
    );
  }, [favouriteTemplateId, dispatch]);

  const handleTemplateChange = useCallback(
    value => {
      dispatch(exportTemplateActions.templateSelectionChanged(value ?? 'all'));
    },
    [dispatch]
  );

  useEffect(() => {
    if (error) {
      if (error.status === 412) {
        regenerateTransactionId();
      }

      handleFailedExportNotification(error.status);
      onClose();
    }
  }, [error, onClose, regenerateTransactionId]);

  const handleEditExportTemplates = useCallback(() => {
    if (selectedTemplateId === 'all') {
      dispatch(
        exportTemplateActions.newTemplateEditStarted({
          context: 'search',
          hasLicense,
        })
      );
    } else {
      fetchSingleTemplate({ url: exportTemplate(selectedTemplateId) });
    }
  }, [dispatch, selectedTemplateId, fetchSingleTemplate, hasLicense]);

  const exportTimeoutId = useRef(null);
  const handleFileNameUpdate = useCallback(value => {
    setFileName(f => (/^[a-z0-9-_ ]*$/i.test(value) ? value : f));
  }, []);

  const handlePreviouslyExportedChange = value => {
    if (value && excludePreviouslyExported === null)
      setShowExcludePreviouslyExportedMessage(true);

    setExcludePreviouslyExported(value);
  };

  const handleExport = useCallback(() => {
    onClose();
    if (exportTimeoutId.current) {
      clearTimeout(exportTimeoutId.current);
    }

    const exportParams = {
      downloadType,
      fileName,
      transactionId,
      excludePreviouslyExported:
        excludePreviouslyExported === null ? false : excludePreviouslyExported,
      excludeEmptyTabsEnabled,
      excludeAcademic: !isAcademicFull(user) || reachedAcademicCreditsLimit,
      excludeIndustry: !isIndustryFull(user) || reachedIndustryCreditsLimit,
    };

    if (selectedTemplateId !== 'all' && showExportTemplateSection) {
      exportParams.exportTemplateId = selectedTemplateId;
    }

    exportTimeoutId.current = setTimeout(() => onExport(exportParams), 100);

    return () => {
      if (exportTimeoutId.current) {
        clearTimeout(exportTimeoutId.current);
      }
    };
  }, [
    onClose,
    excludeEmptyTabsEnabled,
    onExport,
    fileName,
    transactionId,
    user,
    reachedAcademicCreditsLimit,
    reachedIndustryCreditsLimit,
    excludePreviouslyExported,
    downloadType,
    selectedTemplateId,
    showExportTemplateSection,
  ]);

  if (checkExportLoading || error) {
    return (
      <Container style={style} className={className}>
        <Loader />
      </Container>
    );
  }

  const showEmailSection = canUseCredits && !user.hideResearcherEmail;

  const displayTotal = excludePreviouslyExported
    ? totalEmails - previouslyExportedEmails
    : totalEmails;

  let realTotalEmails = displayTotal;
  if (reachedAcademicCreditsLimit) {
    realTotalEmails -= academicEmails;
  }
  if (reachedIndustryCreditsLimit) {
    realTotalEmails -= industryEmails;
  }

  const Footer = footer || BaseFooter;
  if (user.disableExport) {
    return (
      <Container className={className}>
        <Alerts disabledExport={user.disableExport} />
        {downloadType !== downloadTypeConstants.crmPush && (
          <Detail
            textTransform='none'
            label='Name your export file'
            className='export-name'
            style={{ marginTop: '15px' }}
            headerStyle={{
              color: colours.black,
              fontWeight: 'normal',
              opacity: '60%',
            }}
          >
            <Input
              onKeyDown={e => e.stopPropagation()}
              placeholder='Type the name of your export here'
              maxLength={50}
              disabled={isFullTrial(user) || user.disableExport}
              onChange={handleFileNameUpdate}
              value={fileName}
            />
          </Detail>
        )}
        <Divider style={{ margin: '15px 0' }} />
        <Footer
          onCancel={onClose}
          onExport={handleExport}
          disabled={
            isFullTrial(user) ||
            user.disableExport ||
            (displayTotal === 0 && noEmailContacts === 0)
          }
        />
      </Container>
    );
  }

  const isAcademicUnlimited = academicCreditsTotal === -1;
  const isIndustryUnlimited = industryCreditsTotal === -1;

  const creditsTotal = () => {
    if (isFullTrial(user)) {
      return (
        (isAcademicUnlimited ? 0 : academicCreditsRequired ?? 0) +
        (isIndustryUnlimited ? 0 : industryCreditsRequired ?? 0)
      );
    }

    return (
      (isAcademicFull(user) && user.showAcademicCredits
        ? isAcademicUnlimited
          ? 0
          : academicCreditsRequired ?? 0
        : 0) +
      (isIndustryFull(user) && user.showIndustryCredits
        ? isIndustryUnlimited
          ? 0
          : industryCreditsRequired ?? 0
        : 0)
    );
  };

  const isIndustryOnlyExportDisabled =
    (downloadType === downloadTypeConstants.vcFunding ||
      downloadType === downloadTypeConstants.subsidiaries) &&
    !isIndustryFull(user);
  const isDisabled =
    !hasFullLicense(user) ||
    user.disableExport ||
    isIndustryOnlyExportDisabled ||
    (isAcademicFull(user) &&
      reachedAcademicCreditsLimit &&
      isIndustryFull(user) &&
      reachedIndustryCreditsLimit) ||
    (isAcademicFull(user) &&
      !isIndustryFull(user) &&
      reachedAcademicCreditsLimit) ||
    (isIndustryFull(user) &&
      !isAcademicFull(user) &&
      reachedIndustryCreditsLimit);

  const CrmLists = crmLists;
  return (
    <Container style={style} className={className}>
      <h2 style={{ fontSize: 12, fontWeight: 600, marginTop: 0 }}>
        Export Summary
      </h2>
      {canUseCredits ? (
        <div>
          {downloadType === downloadTypeConstants.crmPush && CrmLists}
          {showEmailSection && (
            <>
              <Total
                user={user}
                excludePreviouslyExported={excludePreviouslyExported}
                totalEmails={displayTotal}
                academicCreditsRequired={academicEmails}
                industryCreditsRequired={industryEmails}
                previouslyExportedEmails={previouslyExportedEmails}
                academicCreditsUsed={academicCreditsUsed}
                industryCreditsUsed={industryCreditsUsed}
                uncategorisedEmails={uncategorisedEmails}
                credits={creditsTotal()}
                showCredits={
                  user.isOnCreditSystem &&
                  ((!noAcademicLicence(user) && user.showAcademicCredits) ||
                    (user.showIndustryCredits && !noIndustryLicence(user)))
                }
                disabled={isDisabled}
                isAcademicUnlimited={isAcademicUnlimited}
                isIndustryUnlimited={isIndustryUnlimited}
              />
              <div style={{ margin: '15px 0' }} />
              {user.isOnCreditSystem &&
                (user.showAcademicCredits || user.showIndustryCredits) && (
                  <StatsProgress
                    showAcademicCredits={
                      user.showAcademicCredits && isAcademicFull(user)
                    }
                    academicCreditsUsed={academicCreditsUsed}
                    academicCreditsRequired={academicCreditsRequired}
                    academicCreditsTotal={academicCreditsTotal}
                    showIndustryCredits={
                      user.showIndustryCredits && isIndustryFull(user)
                    }
                    industryCreditsUsed={industryCreditsUsed}
                    industryCreditsRequired={industryCreditsRequired}
                    industryCreditsTotal={industryCreditsTotal}
                    renewDays={daysUntilReset}
                  />
                )}
              <Alerts
                isOnCreditSystem={user.isOnCreditSystem}
                aboutToReachAcademicLimits={isAboutToReachAcademicCreditsLimit}
                aboutToReachIndustryLimits={isAboutToReachIndustryCreditsLimit}
                reachedAcademicLimits={reachedAcademicCreditsLimit}
                reachedIndustryLimits={reachedIndustryCreditsLimit}
                disabledExport={user.disableExport}
                showAcademicCredits={user.showAcademicCredits}
                showIndustryCredits={user.showIndustryCredits}
                noExportEmails={realTotalEmails === 0}
                noExportContacts={
                  realTotalEmails === 0 && noEmailContacts === 0
                }
              />
              {showExcludePreviouslyExportedMessage && (
                <PreviouslyExportedMessage
                  setShowPrevouslyExportedMessage={
                    setShowExcludePreviouslyExportedMessage
                  }
                />
              )}
            </>
          )}
        </div>
      ) : (
        <>
          {(isIndustryOnlyExportDisabled || isFullTrial(user)) && (
            <Alert
              type='info'
              showIcon
              style={{ fontSize: '12px', marginBottom: '15px' }}
              message={
                isIndustryOnlyExportDisabled
                  ? isFullTrial(user)
                    ? industryTrial
                    : nonIndustry
                  : allTrial
              }
            />
          )}
        </>
      )}
      <Divider style={{ marginTop: 15, marginBottom: 15 }} />
      {/* Export Summary Settings */}
      <div
        id='export-summary-file-details'
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 12, fontWeight: 600 }}>
          File Details
        </h2>
        {canUseCredits && (
          <div
            id='export-summary-settings-toggles'
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
            }}
          >
            {showEmailSection && (
              <Checkbox
                className='exclude-previous-checkbox'
                disabled={isDisabled}
                style={{ width: '100%' }}
                labelStyle={{ fontSize: 12 }}
                checked={excludePreviouslyExported}
                onChange={value => handlePreviouslyExportedChange(value)}
                labelText='Exclude previously exported emails'
              />
            )}
            {downloadType !== downloadTypeConstants.crmPush && (
              <Checkbox
                className='exclude-previous-checkbox'
                checked={excludeEmptyTabsEnabled}
                style={{ width: '100%' }}
                labelStyle={{ fontSize: 12 }}
                onChange={value => onExcludeEmptyTabsCheckboxChange(value)}
                labelText='Exclude tabs with no results'
              />
            )}
          </div>
        )}
        {downloadType !== downloadTypeConstants.crmPush && (
          <div>
            <label
              for='export-summary-file-name'
              style={{ fontSize: 12, color: colours.text }}
            >
              Name your export file
            </label>
            <Input
              id='export-summary-file-name'
              onKeyDown={e => e.stopPropagation()}
              placeholder='Type the name of your export here'
              maxLength={50}
              disabled={
                isDisabled ||
                (!user.hideResearcherEmail &&
                  realTotalEmails === 0 &&
                  noEmailContacts === 0)
              }
              onChange={handleFileNameUpdate}
              value={fileName}
            />
          </div>
        )}
        {/* Export Summary Template Selection */}
        {showExportTemplateSection && (
          <div>
            <label
              for='export-summary-template-select'
              style={{ fontSize: 12, color: colours.text }}
            >
              Export Template
            </label>
            <div data-testid='export-template-select-wrapper'>
              <Select
                data-testid='export-template-select'
                id='export-summary-template-select'
                style={{ width: '100%' }}
                value={selectedTemplateId}
                onChange={handleTemplateChange}
                onKeyDown={e => e.stopPropagation()}
                options={templateOptions}
                loading={metadataRes.loading || minimalListRes.loading}
                getPopupContainer={triggerNode => triggerNode.parentElement}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option.searchText.includes(deburr(input.toLowerCase()))
                }
              />
            </div>
            <CustomiseFieldsLink
              onClick={handleEditExportTemplates}
              disabled={isDisabled}
            >
              Customise fields
            </CustomiseFieldsLink>
          </div>
        )}
      </div>
      {/* Footer */}
      <Divider style={{ margin: '15px 0' }} />
      <Footer
        onCancel={onClose}
        onExport={handleExport}
        disabled={
          isDisabled ||
          (!user.hideResearcherEmail &&
            realTotalEmails === 0 &&
            noEmailContacts === 0)
        }
        tooltip={
          isDisabled
            ? ''
            : isAcademicFull(user) && reachedAcademicCreditsLimit
            ? excludeAcademic(user.showAcademicCredits)
            : isIndustryFull(user) && reachedIndustryCreditsLimit
            ? excludeIndustry(user.showIndustryCredits)
            : ''
        }
      />
    </Container>
  );
}

Summary.propTypes = {
  transactionId: PropTypes.string,
  regenerateTransactionId: PropTypes.func,
  downloadType: PropTypes.oneOf(
    Object.keys(downloadTypeConstants).map(k => downloadTypeConstants[k])
  ),
  onClose: PropTypes.func.isRequired,
  getQueryBody: PropTypes.func,
  onExport: PropTypes.func.isRequired,
  crmLists: PropTypes.element,
  footer: PropTypes.func,
  className: PropTypes.node,
  style: PropTypes.shape({}),
  showExportTemplateSection: PropTypes.bool,
};

export default Summary;
